import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Crown, BadgeCheck, Loader2, TrendingUp, Package, Users, Trophy, Camera, X, Gift, CalendarDays } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { useStore } from "./store";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const Stars = ({ value, size = 13, id }) => (
  <span data-testid={id} className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={size} className={n <= Math.round(value) ? "fill-[#c5a059] text-[#c5a059]" : "text-[#c5a059]/30"} />
    ))}
  </span>
);

const photoUrl = (id) => `${API}/review-photos/${id}`;

const ReviewPhotos = ({ r, small }) => {
  const shots = [["Before", r.before_photo], ["After", r.after_photo]].filter(([, id]) => id);
  if (!shots.length) return null;
  return (
    <div className={`mt-3 grid grid-cols-2 gap-2 ${small ? "max-w-[180px]" : "max-w-sm"}`}>
      {shots.map(([label, id]) => (
        <figure key={id} className="relative overflow-hidden rounded-sm border border-white/10">
          <img data-testid={`review-photo-${label.toLowerCase()}-${r.id}`} src={photoUrl(id)} alt={`${label} photo`} loading="lazy" className="aspect-square w-full object-cover" />
          <figcaption className="absolute bottom-0 left-0 bg-black/75 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-[#c5a059]">{label}</figcaption>
        </figure>
      ))}
    </div>
  );
};

const ReviewList = ({ list }) => (
  <div className="max-h-[60vh] overflow-y-auto divide-y divide-white/10">
    {list.map((r) => (
      <div key={r.id} data-testid={`review-${r.id}`} className="py-4">
        <div className="flex items-center justify-between gap-3">
          <Stars value={r.rating} />
          <span className="text-[11px] text-[#f5f5f0]/45">{(r.created_at || "").slice(0, 10)}</span>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-[#f5f5f0]/85">{r.body}</p>
        <ReviewPhotos r={r} />
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#f5f5f0]/60">
          {r.name} <BadgeCheck size={13} className="text-[#c5a059]" /> <span className="text-[#c5a059]">Verified Buyer</span>
          {r.vip && <span data-testid={`review-vip-${r.id}`} className="ml-1 inline-flex items-center gap-1 rounded-sm border border-[#c5a059]/50 px-1.5 py-px text-[10px] uppercase tracking-[0.15em] text-[#d4af37]"><Crown size={10} /> VIP King</span>}
        </p>
      </div>
    ))}
  </div>
);

export const ProductMeta = ({ p, i }) => {
  const { reviews, stock } = useStore();
  const [open, setOpen] = useState(false);
  const list = reviews.filter((r) => r.sku === p.sku);
  const avg = list.length ? list.reduce((a, r) => a + r.rating, 0) / list.length : 0;
  const { vip, launch } = useStore();
  const st = stock[p.sku];
  const early = vip && (launch.vip_only || []).includes(p.sku);
  if (!list.length && !st && !early) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
      {list.length > 0 && (
        <button data-testid={`product-reviews-${i}`} onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-xs text-[#f5f5f0]/70 hover:text-[#c5a059] transition-colors">
          <Stars value={avg} size={12} /> <span>{avg.toFixed(1)} · {list.length} review{list.length > 1 ? "s" : ""}</span>
        </button>
      )}
      {early && <span data-testid={`product-vip-early-${i}`} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-[#d4af37]"><Crown size={11} /> VIP Early Access</span>}
      {st && (
        <span data-testid={`product-stock-${i}`} className="text-[11px] uppercase tracking-[0.15em] text-[#d4af37]">
          {st === "out" ? "Sold out — restocking soon" : "Running low. Kings move first."}
        </span>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0d0d0d] border-[#c5a059]/30 text-[#f5f5f0] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{p.name}</DialogTitle>
            <DialogDescription className="flex items-center gap-2 text-[#f5f5f0]/60"><Stars value={avg} /> {avg.toFixed(1)} from {list.length} verified buyer{list.length > 1 ? "s" : ""}</DialogDescription>
          </DialogHeader>
          <ReviewList list={list} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StarPicker = ({ value, onChange, id }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" data-testid={`${id}-star-${n}`} onClick={() => onChange(n)} aria-label={`${n} stars`}>
        <Star size={26} className={n <= value ? "fill-[#c5a059] text-[#c5a059]" : "text-[#c5a059]/35 hover:text-[#c5a059]"} />
      </button>
    ))}
  </div>
);

const shrink = (file) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const k = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => resolve(b || file), "image/jpeg", 0.85);
    URL.revokeObjectURL(img.src);
  };
  img.onerror = () => resolve(file);
  img.src = URL.createObjectURL(file);
});

const PhotoSlot = ({ label, token, value, onChange, id }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) { toast.error("Please choose a JPG, PNG or WebP photo."); return; }
    setBusy(true);
    try {
      const blob = await shrink(f);
      const fd = new FormData();
      fd.append("token", token);
      fd.append("file", blob, "photo.jpg");
      const r = await axios.post(`${API}/reviews/photo`, fd);
      onChange(r.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed. Please try again.");
    }
    setBusy(false);
  };
  return (
    <div className="relative">
      {value ? (
        <div className="relative overflow-hidden rounded-sm border border-[#c5a059]/40">
          <img data-testid={`${id}-preview`} src={photoUrl(value)} alt={label} className="aspect-square w-full object-cover" />
          <span className="absolute bottom-0 left-0 bg-black/75 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-[#c5a059]">{label}</span>
          <button data-testid={`${id}-remove`} type="button" onClick={() => onChange(null)} className="absolute top-1.5 right-1.5 rounded-full bg-black/80 p-1 text-[#f5f5f0] hover:text-[#c5a059]"><X size={14} /></button>
        </div>
      ) : (
        <label data-testid={`${id}-label`} className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-white/20 text-xs text-[#f5f5f0]/60 hover:border-[#c5a059] hover:text-[#c5a059] transition-colors">
          {busy ? <Loader2 size={20} className="animate-spin" /> : <Camera size={20} />}
          <span>{busy ? "Uploading…" : `Add ${label} photo`}</span>
          <input data-testid={`${id}-input`} type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} className="hidden" disabled={busy} />
        </label>
      )}
    </div>
  );
};

const ReviewForm = ({ token, item, defaultName, onDone }) => {
  const [rating, setRating] = useState(5);
  const [name, setName] = useState(defaultName);
  const [body, setBody] = useState("");
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/reviews`, { token, sku: item.sku, rating, name, body, before_photo: before, after_photo: after });
      toast.success("Thank you, King. Your review is live.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "Could not save your review.");
    }
    setBusy(false);
  };
  return (
    <div data-testid={`review-form-${item.sku}`} className="rounded-sm border border-white/10 bg-[#121212] p-6">
      <h2 className="font-serif text-xl">{item.name}</h2>
      <div className="mt-4"><StarPicker value={rating} onChange={setRating} id={`review-${item.sku}`} /></div>
      <input data-testid={`review-name-${item.sku}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your first name & last initial" className="mt-4 w-full rounded-sm bg-black border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-[#c5a059]" />
      <textarea data-testid={`review-body-${item.sku}`} value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="How did it work for you?" className="mt-3 w-full rounded-sm bg-black border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-[#c5a059]" />
      <p className="mt-4 text-xs uppercase tracking-[0.15em] text-[#c5a059]">Before & after <span className="normal-case tracking-normal text-[#f5f5f0]/50">(optional)</span></p>
      <div className="mt-2 grid grid-cols-2 gap-3 max-w-xs">
        <PhotoSlot label="Before" token={token} value={before} onChange={setBefore} id={`review-before-${item.sku}`} />
        <PhotoSlot label="After" token={token} value={after} onChange={setAfter} id={`review-after-${item.sku}`} />
      </div>
      <button data-testid={`review-submit-${item.sku}`} disabled={busy || body.trim().length < 5 || !name.trim()} onClick={submit} className="mt-4 inline-flex items-center gap-2 rounded-sm bg-[#c5a059] px-6 py-3 text-sm font-semibold text-black hover:bg-[#d4af37] disabled:opacity-50 transition-colors">
        {busy && <Loader2 size={15} className="animate-spin" />} Post Review
      </button>
    </div>
  );
};

export const ReviewPage = () => {
  const token = new URLSearchParams(window.location.search).get("t");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = () => axios.get(`${API}/reviews/order/${token}`).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || "This review link isn't valid."));
  useEffect(() => { if (token) load(); else setErr("This review link isn't valid."); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pending = data ? data.items.filter((i) => !i.reviewed) : [];
  return (
    <div data-testid="review-page" className="min-h-screen bg-[#0a0a0a] text-[#f5f5f0] px-6 py-16">
      <div className="max-w-2xl mx-auto">
        <Crown className="text-[#c5a059]" size={32} />
        <h1 className="mt-6 font-serif text-4xl sm:text-5xl">Your word carries weight.</h1>
        <p className="mt-4 text-[#f5f5f0]/70">Tell other kings how your routine is working. Every review carries the Verified Buyer seal.</p>
        {err && <p data-testid="review-error" className="mt-10 text-[#d4af37]">{err}</p>}
        {data && !pending.length && <p data-testid="review-all-done" className="mt-10 text-[#d4af37]">All done — thank you, King. Long live the crown.</p>}
        <div className="mt-10 space-y-6">
          {pending.map((it) => <ReviewForm key={it.sku} token={token} item={it} defaultName={data.name} onDone={load} />)}
        </div>
        <a data-testid="review-back-home" href="/" className="mt-10 inline-block text-sm text-[#c5a059] underline">Back to the Kingdom</a>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, sub, id }) => (
  <div className="rounded-sm border border-[#c5a059]/25 bg-[#121212] p-5">
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#c5a059]"><Icon size={14} /> {label}</div>
    <div data-testid={id} className="mt-3 font-serif text-3xl text-[#f5f5f0]">{value}</div>
    {sub && <div className="mt-1 text-xs text-[#f5f5f0]/55">{sub}</div>}
  </div>
);

export const AdminDashboard = ({ d }) => (
  <div data-testid="admin-dashboard" className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
    <Stat icon={TrendingUp} id="dash-today-sales" label="Today's Sales" value={money(d.today_sales)} sub={`${d.today_orders} order${d.today_orders === 1 ? "" : "s"} today`} />
    <Stat icon={Package} id="dash-total-sales" label="All-Time Sales" value={money(d.total_sales)} sub={`${d.total_orders} paid orders`} />
    <Stat icon={Trophy} id="dash-best-seller" label="Best Seller" value={d.best_seller ? d.best_seller.units : "—"} sub={d.best_seller ? d.best_seller.name : "No sales yet"} />
    <Stat icon={Users} id="dash-spots-left" label="Spots Left" value={`${d.spots_left}/100`} sub="First 100 Kings" />
  </div>
);

const StockRow = ({ s, token, level, onDone }) => {
  const [val, setVal] = useState(s.stock ?? "");
  const save = async () => {
    try {
      await axios.put(`${API}/admin/inventory`, { sku: s.sku, stock: Number(val) }, { headers: { "X-Admin-Token": token } });
      toast.success(`${s.name}: ${val} in stock`);
      onDone();
    } catch {
      toast.error("Could not save stock.");
    }
  };
  return (
    <div data-testid={`stock-row-${s.sku}`} className="flex flex-wrap items-center justify-between gap-3 py-3 border-t border-white/5">
      <span className="text-sm">{s.name}{s.low && <span data-testid={`stock-low-${s.sku}`} className="ml-2 rounded-sm bg-[#c5a059] px-2 py-0.5 text-[10px] font-bold uppercase text-black">Low — reorder</span>}</span>
      <span className="flex items-center gap-2">
        <input data-testid={`stock-input-${s.sku}`} type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Not tracked" className="w-28 rounded-sm bg-black border border-white/15 px-2 py-1.5 text-sm" />
        <button data-testid={`stock-save-${s.sku}`} disabled={val === ""} onClick={save} className="rounded-sm border border-[#c5a059]/50 px-3 py-1.5 text-xs text-[#c5a059] hover:bg-[#c5a059]/10 disabled:opacity-40">Save</button>
      </span>
    </div>
  );
};

export const InventoryCard = ({ d, token, onDone }) => (
  <div data-testid="admin-inventory-card" className="mt-4 rounded-sm border border-[#c5a059]/30 bg-[#121212] p-5">
    <div className="text-xs uppercase tracking-widest text-[#c5a059]">Inventory</div>
    <p className="mt-1 text-sm text-[#f5f5f0]/72">Enter what you have on hand. Every sale counts down (bundles count each product inside). You get an email at {d.low_stock_level} left, and shoppers only see "Running low" once stock really is at {d.low_stock_level} or less.</p>
    <div className="mt-3">{d.stock.map((s) => <StockRow key={s.sku} s={s} token={token} level={d.low_stock_level} onDone={onDone} />)}</div>
  </div>
);

export const ReviewsAdmin = ({ reviews, token, onDone }) => {
  const act = async (r, kind) => {
    try {
      const h = { headers: { "X-Admin-Token": token } };
      if (kind === "delete") await axios.delete(`${API}/admin/reviews/${r.id}`, h);
      else await axios.put(`${API}/admin/reviews/${r.id}`, { hidden: !r.hidden }, h);
      onDone();
    } catch {
      toast.error("Could not update the review.");
    }
  };
  return (
    <>
      <h2 className="mt-10 font-serif text-xl text-[#f5f5f0]">Reviews ({reviews.length})</h2>
      <p className="mt-1 text-sm text-[#f5f5f0]/60">Verified-buyer reviews go live right away. Hide or delete any you don't want shown.</p>
      <div data-testid="admin-reviews-table" className="mt-4 rounded-sm border border-white/10 divide-y divide-white/5">
        {reviews.length === 0 && <p className="px-4 py-6 text-center text-sm text-[#f5f5f0]/50">No reviews yet.</p>}
        {reviews.map((r) => (
          <div key={r.id} data-testid={`admin-review-${r.id}`} className={`flex flex-wrap items-start justify-between gap-3 px-4 py-3 ${r.hidden ? "opacity-50" : ""}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-xs text-[#f5f5f0]/60"><Stars value={r.rating} size={12} /> {r.name} · {r.sku.replace(/_/g, " ")} {r.hidden && "· hidden"}</div>
              <p className="mt-1 text-sm">{r.body}</p>
              <ReviewPhotos r={r} small />
            </div>
            <div className="flex gap-2">
              <button data-testid={`admin-review-toggle-${r.id}`} onClick={() => act(r, "toggle")} className="rounded-sm border border-white/15 px-3 py-1.5 text-xs hover:border-[#c5a059]">{r.hidden ? "Show" : "Hide"}</button>
              <button data-testid={`admin-review-delete-${r.id}`} onClick={() => act(r, "delete")} className="rounded-sm border border-red-500/40 px-3 py-1.5 text-xs text-red-300 hover:bg-red-500/10">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export const LeaderboardCard = ({ rows }) => (
  <>
    <h2 className="mt-10 font-serif text-xl text-[#f5f5f0]">Top Referrers</h2>
    <p className="mt-1 text-sm text-[#f5f5f0]/60">Your biggest fans — the kings bringing in new kings.</p>
    <div data-testid="admin-leaderboard" className="mt-4 overflow-x-auto rounded-sm border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-[#121212] text-left text-[#c5a059]"><tr><th className="px-4 py-3">#</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Friends Who Bought</th><th className="px-4 py-3">Sales Brought In</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-[#f5f5f0]/50">No referral sales yet.</td></tr>}
          {rows.map((b, i) => (
            <tr key={b.ref_code} data-testid={`leaderboard-row-${i}`} className="border-t border-white/5">
              <td className="px-4 py-3">{i === 0 ? <Crown size={15} className="text-[#c5a059]" /> : i + 1}</td>
              <td className="px-4 py-3">{b.email}<div className="text-xs text-[#f5f5f0]/45">{b.ref_code}</div></td>
              <td className="px-4 py-3">{b.friends}</td>
              <td className="px-4 py-3 text-[#c5a059]">{money(b.sales)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
);

const SKU_NAMES = {
  creamy_balm: "Creamy Cleansing Balm", oil_free_moisturizer: "Oil-Free Moisturizer", mineral_spf50: "Mineral SPF 50",
  blue_clay_mask: "Blue Clay Exfoliation Mask", ingrown_serum: "Ingrown Hair Serum", bundle_clean_shaven: "The Polished Prince",
  bundle_bearded: "The Majestic Mane", bundle_smooth: "The Regal Glow",
};

export const ReviewHighlights = () => {
  const { reviews } = useStore();
  const top = reviews.filter((r) => r.rating >= 4 && (r.before_photo || r.after_photo)).slice(0, 3);
  if (!top.length) return null;
  return (
    <section data-testid="review-highlights" className="relative py-24 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#c5a059]">Real Kings · Real Results</p>
        <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-[#f5f5f0] max-w-2xl">The proof is in the mirror.</h2>
        <div className={`mt-14 grid gap-8 ${top.length === 3 ? "md:grid-cols-3" : top.length === 2 ? "md:grid-cols-2 max-w-4xl" : "max-w-md"}`}>
          {top.map((r) => (
            <figure key={r.id} data-testid={`highlight-${r.id}`} className="group rounded-sm border border-white/10 bg-[#0f0f0f] p-6 hover:border-[#c5a059]/40 transition-colors duration-500">
              <ReviewPhotos r={r} />
              <div className="mt-5"><Stars value={r.rating} /></div>
              <blockquote className="mt-3 font-serif text-lg leading-snug text-[#f5f5f0]">"{r.body}"</blockquote>
              <figcaption className="mt-4 flex flex-wrap items-center gap-1.5 text-xs text-[#f5f5f0]/60">
                {r.name} <BadgeCheck size={13} className="text-[#c5a059]" /> <span className="text-[#c5a059]">Verified Buyer</span>
                {r.vip && <span data-testid={`highlight-vip-${r.id}`} className="inline-flex items-center gap-1 rounded-sm border border-[#c5a059]/50 px-1.5 py-px text-[10px] uppercase tracking-[0.15em] text-[#d4af37]"><Crown size={10} /> VIP King</span>}
                <span className="text-[#f5f5f0]/40">· {SKU_NAMES[r.sku] || ""}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export const VipCard = ({ vips, token }) => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const h = { headers: { "X-Admin-Token": token } };
  const send = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/vip-email`, { subject, message }, h);
      toast.success(`Early-access email going out to ${r.data.count} VIP King${r.data.count > 1 ? "s" : ""}.`);
      setSubject(""); setMessage("");
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "Could not send.");
    }
    setBusy(false);
  };
  const report = async () => {
    try {
      await axios.post(`${API}/admin/weekly-report/test`, {}, h);
      toast.success("This week's report was sent to your inbox.");
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "Could not send the report.");
    }
  };
  return (
    <>
      <h2 className="mt-10 font-serif text-xl text-[#f5f5f0] flex items-center gap-2"><Crown size={18} className="text-[#c5a059]" /> VIP Kings Club ({vips.length})</h2>
      <p className="mt-1 text-sm text-[#f5f5f0]/60">Customers with 3+ orders. They get a VIP badge on their reviews, a welcome email, and your early-access announcements.</p>
      <div data-testid="admin-vip-list" className="mt-4 rounded-sm border border-white/10 divide-y divide-white/5">
        {vips.length === 0 && <p className="px-4 py-6 text-center text-sm text-[#f5f5f0]/50">No VIP Kings yet.</p>}
        {vips.map((v) => (
          <div key={v.email} className="flex flex-wrap justify-between gap-2 px-4 py-3 text-sm">
            <span>{v.name || v.email}<span className="ml-2 text-xs text-[#f5f5f0]/50">{v.email}</span></span>
            <span className="text-[#c5a059]">{v.orders} orders · {money(v.spent)}</span>
          </div>
        ))}
      </div>
      <div data-testid="admin-vip-email" className="mt-4 rounded-sm border border-[#c5a059]/30 bg-[#121212] p-5">
        <div className="text-xs uppercase tracking-widest text-[#c5a059]">Early-Access Email to VIPs</div>
        <input data-testid="vip-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Headline, e.g. New: Royal Beard Oil — VIPs shop first" className="mt-3 w-full rounded-sm bg-black border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#c5a059]" />
        <textarea data-testid="vip-email-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Your message to your VIP Kings" className="mt-2 w-full rounded-sm bg-black border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#c5a059]" />
        <div className="mt-3 flex flex-wrap gap-3">
          <button data-testid="vip-email-send" disabled={busy || subject.length < 3 || message.length < 10 || !vips.length} onClick={send} className="rounded-sm bg-[#c5a059] px-5 py-2.5 text-sm font-semibold text-black hover:bg-[#d4af37] disabled:opacity-50 transition-colors">Send to VIPs</button>
          <button data-testid="weekly-report-test" onClick={report} className="rounded-sm border border-[#c5a059]/50 px-5 py-2.5 text-sm text-[#c5a059] hover:bg-[#c5a059]/10 transition-colors">Send me this week's report now</button>
        </div>
        <p className="mt-2 text-xs text-[#f5f5f0]/50">Your weekly report also arrives automatically every Monday at 9am Pacific.</p>
      </div>
    </>
  );
};

const AMOUNTS = [50, 100, 150, 250];
const field = "w-full rounded-sm bg-black border border-white/15 px-3 py-2.5 text-sm text-[#f5f5f0] outline-none focus:border-[#c5a059] placeholder:text-[#f5f5f0]/30";

export const GiftCards = () => {
  const [amount, setAmount] = useState(100);
  const [f, setF] = useState({ sender_name: "", recipient_name: "", recipient_email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ok = f.sender_name.trim() && f.recipient_name.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.recipient_email);
  const buy = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/giftcards/checkout`, { ...f, amount, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "Couldn't start checkout.");
      setBusy(false);
    }
  };
  return (
    <section id="gift-cards" data-testid="gift-cards" className="scroll-mt-48 sm:scroll-mt-28 relative py-24 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid gap-14 lg:grid-cols-2 items-center">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#c5a059]">The Royal Gift Card</p>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-[#f5f5f0]">Crown someone you respect.</h2>
          <p className="mt-5 max-w-md text-[#f5f5f0]/70">For fathers, husbands, brothers and friends. Delivered instantly by email in black and gold. Never expires, and any balance carries over.</p>
          <div className="mt-10 relative aspect-[1.6] max-w-md rounded-xl border border-[#c5a059]/50 bg-gradient-to-br from-[#161310] via-[#0b0b0b] to-[#1a1610] p-7 shadow-[0_30px_80px_-30px_rgba(197,160,89,0.45)]">
            <img data-testid="gift-card-logo" loading="lazy" decoding="async" src="/logo-640.webp" alt="Royal King Skin Co. crest" className="absolute right-5 top-5 h-32 sm:h-40 w-auto" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#c5a059]">Gift Card</div>
            <div data-testid="gift-card-preview-amount" className="absolute bottom-7 left-7 font-serif text-5xl text-[#d4af37]">${amount}</div>
            <div className="absolute bottom-8 right-7 text-[10px] uppercase tracking-[0.2em] text-[#f5f5f0]/50">Royal King Skin Co.</div>
          </div>
        </div>
        <div className="rounded-sm border border-white/10 bg-[#0f0f0f] p-7">
          <div className="grid grid-cols-4 gap-2">
            {AMOUNTS.map((a) => (
              <button key={a} data-testid={`gift-amount-${a}`} onClick={() => setAmount(a)} className={`rounded-sm border py-3 font-serif text-lg transition-colors ${amount === a ? "border-[#c5a059] bg-[#c5a059] text-black" : "border-white/15 text-[#f5f5f0] hover:border-[#c5a059]"}`}>${a}</button>
            ))}
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <input data-testid="gift-sender" value={f.sender_name} onChange={set("sender_name")} placeholder="Your name" className={field} />
            <input data-testid="gift-recipient-name" value={f.recipient_name} onChange={set("recipient_name")} placeholder="Recipient's name" className={field} />
          </div>
          <input data-testid="gift-recipient-email" type="email" value={f.recipient_email} onChange={set("recipient_email")} placeholder="Recipient's email" className={`mt-3 ${field}`} />
          <textarea data-testid="gift-message" value={f.message} onChange={set("message")} maxLength={300} rows={3} placeholder="Personal message (optional)" className={`mt-3 ${field}`} />
          <button data-testid="gift-buy" disabled={!ok || busy} onClick={buy} className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] py-4 text-sm font-semibold text-black hover:bg-[#d4af37] disabled:opacity-50 transition-colors">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Gift size={16} />} Send a ${amount} Gift Card
          </button>
        </div>
      </div>
    </section>
  );
};

const PhotoRow = ({ sku, token, url, vipOnly, onDone }) => {
  const [busy, setBusy] = useState(false);
  const h = { headers: { "X-Admin-Token": token } };
  const up = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("sku", sku);
      fd.append("file", await shrink(file), "photo.jpg");
      await axios.post(`${API}/admin/product-photo`, fd, h);
      toast.success(`${SKU_NAMES[sku]} photo updated`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString() || "Upload failed.");
    }
    setBusy(false);
  };
  const reset = async () => { await axios.delete(`${API}/admin/product-photo/${sku}`, h); toast.success("Back to the original photo"); onDone(); };
  const toggleVip = async () => {
    const next = vipOnly.includes(sku) ? vipOnly.filter((x) => x !== sku) : [...vipOnly, sku];
    await axios.put(`${API}/admin/vip-only`, { skus: next }, h);
    toast.success(vipOnly.includes(sku) ? "Now visible to everyone" : "Now VIP early access only");
    onDone();
  };
  return (
    <div data-testid={`photo-row-${sku}`} className="flex flex-wrap items-center justify-between gap-3 py-3 border-t border-white/5">
      <span className="flex items-center gap-3 text-sm">
        {url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="h-10 w-10 rounded-sm object-cover border border-[#c5a059]/40" /> : <span className="h-10 w-10 rounded-sm border border-dashed border-white/15" />}
        {SKU_NAMES[sku]}
      </span>
      <span className="flex flex-wrap items-center gap-2">
        {vipOnly && <label className="inline-flex items-center gap-1.5 text-xs text-[#f5f5f0]/70 cursor-pointer">
          <input data-testid={`vip-only-${sku}`} type="checkbox" checked={vipOnly.includes(sku)} onChange={toggleVip} className="accent-[#c5a059]" /> VIP only
        </label>}
        <label data-testid={`photo-upload-${sku}`} className="cursor-pointer rounded-sm border border-[#c5a059]/50 px-3 py-1.5 text-xs text-[#c5a059] hover:bg-[#c5a059]/10">
          {busy ? "Uploading…" : "Change photo"}
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={up} className="hidden" disabled={busy} />
        </label>
        {url && <button data-testid={`photo-reset-${sku}`} onClick={reset} className="rounded-sm border border-white/15 px-3 py-1.5 text-xs hover:border-[#c5a059]">Use original</button>}
      </span>
    </div>
  );
};


export const ProductPhotosCard = ({ token, vipOnly, onDone }) => {
  const [photos, setPhotos] = useState({});
  const refresh = () => { axios.get(`${API}/product-photos`).then((r) => setPhotos(r.data)).catch(() => {}); onDone(); };
  useEffect(() => { axios.get(`${API}/product-photos`).then((r) => setPhotos(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-photos-card" className="mt-4 rounded-sm border border-[#c5a059]/30 bg-[#121212] p-5">
      <div className="text-xs uppercase tracking-widest text-[#c5a059]">Product Photos & VIP Early Access</div>
      <p className="mt-1 text-sm text-[#f5f5f0]/72">Swap in a new product photo any time (square photos look best). Tick "VIP only" to hide a product from the public and show it only to VIP Kings through their private link.</p>
      <div className="mt-3">{Object.keys(SKU_NAMES).map((k) => <PhotoRow key={k} sku={k} token={token} url={photos[k]} vipOnly={vipOnly} onDone={refresh} />)}</div>
    </div>
  );
};

export const GiftCardsTable = ({ cards }) => (
  <>
    <h2 className="mt-10 font-serif text-xl text-[#f5f5f0]">Gift Cards ({cards.length})</h2>
    <div data-testid="admin-giftcards-table" className="mt-4 overflow-x-auto rounded-sm border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-[#121212] text-left text-[#c5a059]"><tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">From → To</th><th className="px-4 py-3">Value</th><th className="px-4 py-3">Balance</th></tr></thead>
        <tbody>
          {cards.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-[#f5f5f0]/50">No gift cards sold yet.</td></tr>}
          {cards.map((c) => (
            <tr key={c.code} className="border-t border-white/5">
              <td className="px-4 py-3 font-mono text-xs">{c.code}</td>
              <td className="px-4 py-3">{c.sender_name} → {c.recipient_name}<div className="text-xs text-[#f5f5f0]/50">{c.recipient_email}</div></td>
              <td className="px-4 py-3">{money(c.initial)}</td>
              <td className="px-4 py-3 text-[#c5a059]">{money(c.balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
);

const HolidayCountdown = ({ c }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const ms = Math.max(0, new Date(c.deadline).getTime() - now);
  if (!ms) return null;
  const parts = [["Days", Math.floor(ms / 864e5)], ["Hours", Math.floor(ms / 36e5) % 24], ["Min", Math.floor(ms / 6e4) % 60], ["Sec", Math.floor(ms / 1e3) % 60]];
  return (
    <div data-testid="holiday-countdown" className="mt-8">
      <p className="text-[11px] uppercase tracking-[0.3em] text-[#f5f5f0]/55">{c.deadline_label}<span className="hidden sm:inline"> · </span><span data-testid="holiday-deadline-day" className="block sm:inline mt-1 sm:mt-0 text-[#c5a059]">{c.deadline_day}</span></p>
      <div className="mt-3 flex gap-3">
        {parts.map(([l, v]) => (
          <div key={l} data-testid={`holiday-countdown-${l.toLowerCase()}`} className="w-[4.5rem] rounded-sm border border-[#c5a059]/35 bg-black/50 py-3 text-center">
            <div className="font-serif text-3xl tabular-nums text-[#f5f5f0]">{String(v).padStart(2, "0")}</div>
            <div className="mt-1 text-[9px] uppercase tracking-[0.25em] text-[#c5a059]/80">{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const HolidayBanner = () => {
  const [c, setC] = useState(null);
  useEffect(() => { axios.get(`${API}/campaign`).then((r) => setC(r.data.active ? r.data : null)).catch(() => {}); }, []);
  if (!c) return null;
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <section data-testid="holiday-banner" className="relative overflow-hidden border-t border-[#c5a059]/25 bg-[#0d0b08]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(197,160,89,0.18),transparent_60%)]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 grid gap-12 lg:grid-cols-[1.1fr_1fr] items-center">
        <div>
          <p data-testid="holiday-eyebrow" className="text-[11px] uppercase tracking-[0.3em] text-[#c5a059]">{c.eyebrow}</p>
          <h2 data-testid="holiday-title" className="mt-4 font-serif text-4xl sm:text-5xl text-[#f5f5f0]">{c.title}</h2>
          <p className="mt-5 max-w-lg text-[#f5f5f0]/70">{c.body}</p>
          {c.deadline && <HolidayCountdown c={c} />}
          <div className="mt-8 flex flex-wrap gap-3">
            <button data-testid="holiday-shop-bundles" onClick={() => go("products")} className="rounded-sm bg-[#c5a059] px-7 py-3.5 text-sm font-semibold text-black hover:bg-[#d4af37] transition-colors">Shop the Routines</button>
            <button data-testid="holiday-gift-cards" onClick={() => go("gift-cards")} className="inline-flex items-center gap-2 rounded-sm border border-[#c5a059] px-7 py-3.5 text-sm text-[#c5a059] hover:bg-[#c5a059] hover:text-black transition-colors"><Gift size={15} /> Send a Gift Card</button>
          </div>
          <p className="mt-6 text-xs text-[#f5f5f0]/45">The Majestic Mane ships free · Gift cards arrive in minutes — perfect for last-minute kings.</p>
        </div>
        <div className="relative">
          <img src="https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/38ea2121be8f2bb6df83433f8e1657927d6bf5c2696e2458d69e3f6ddee308c7.jpeg" alt="Royal King routines" className="w-full rounded-sm border border-white/10 object-cover aspect-[3/2]" />
          <div className="absolute -bottom-6 -left-4 sm:-left-8 w-44 sm:w-52 aspect-[1.6] rounded-lg border border-[#c5a059]/60 bg-gradient-to-br from-[#161310] to-[#0b0b0b] p-4 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.9)]">
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#c5a059]">Gift Card</div>
            <div className="absolute bottom-3 left-4 font-serif text-3xl text-[#d4af37]">$100</div>
            <img src="/logo-200.webp" alt="" className="absolute right-3 top-3 h-10 w-auto" />
          </div>
        </div>
      </div>
    </section>
  );
};

const campErr = (e) => toast.error(e.response?.data?.detail?.toString() || "Something went wrong.");

const HolidayDatePicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const sel = value ? new Date(`${value}T12:00:00`) : undefined;
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="custom-date" type="button" className="justify-self-start inline-flex items-center gap-2 rounded-sm border border-white/15 bg-black px-3 py-2 text-sm text-[#f5f5f0] hover:border-[#c5a059]">
          <CalendarDays size={14} className="text-[#c5a059]" />
          {sel ? sel.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "Pick the holiday date (for the countdown)"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#121212] border-[#c5a059]/40 text-[#f5f5f0]" align="start">
        <Calendar mode="single" selected={sel} disabled={{ before: new Date() }} onSelect={(d) => { onChange(d ? iso(d) : ""); setOpen(false); }} initialFocus />
      </PopoverContent>
    </Popover>
  );
};

const CustomCampaignForm = ({ c, h, onDone }) => {
  const [f, setF] = useState({ eyebrow: c.eyebrow, title: c.title, body: c.body, subject: c.subject, date: c.date || "" });
  const save = async () => { try { await axios.put(`${API}/admin/campaign/custom`, { ...f, date: f.date || null }, h); toast.success("Custom holiday saved"); onDone(); } catch (e) { campErr(e); } };
  const inp = "w-full rounded-sm border border-white/15 bg-black px-3 py-2 text-sm text-[#f5f5f0] focus:border-[#c5a059] outline-none";
  return (
    <div data-testid="custom-campaign-form" className="mt-3 grid gap-2">
      <input data-testid="custom-eyebrow" className={inp} value={f.eyebrow} onChange={(e) => setF({ ...f, eyebrow: e.target.value })} placeholder="Small gold line, e.g. Memorial Day · May 25" />
      <input data-testid="custom-title" className={inp} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Big headline" />
      <textarea data-testid="custom-body" rows={3} className={inp} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Your message" />
      <HolidayDatePicker value={f.date} onChange={(v) => setF({ ...f, date: v })} />
      <input data-testid="custom-subject" className={inp} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Email subject line" />
      <button data-testid="custom-save" onClick={save} className="justify-self-start rounded-sm bg-[#c5a059] px-4 py-2 text-sm font-semibold text-black hover:bg-[#d4af37]">Save Custom Holiday</button>
    </div>
  );
};

const CampaignRow = ({ c, active, year, h, onDone }) => {
  const live = active === c.id;
  const sent = c.sent_year === year;
  const toggle = async () => { try { await axios.put(`${API}/admin/campaign`, { id: c.id, on: !live }, h); toast.success(!live ? `${c.name} banner is live` : `${c.name} banner hidden`); onDone(); } catch (e) { campErr(e); } };
  const send = async (test) => {
    if (!test && !window.confirm(`Send the ${c.name} email to everyone on the Royal List?`)) return;
    try { const r = await axios.post(`${API}/admin/campaign/send`, { id: c.id, test }, h); toast.success(test ? "Preview sent to your inbox" : `Sending to ${r.data.count} Royal List member${r.data.count === 1 ? "" : "s"}`); onDone(); } catch (e) { campErr(e); }
  };
  const btn = "rounded-sm border border-[#c5a059]/50 px-3 py-1.5 text-xs text-[#c5a059] hover:bg-[#c5a059]/10 disabled:opacity-40";
  return (
    <div data-testid={`campaign-row-${c.id}`} className="py-3 border-t border-white/5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm">{c.name}{live && <span className="ml-2 rounded-sm bg-[#c5a059] px-2 py-0.5 text-[10px] font-semibold uppercase text-black">Live</span>}<span className="block text-xs text-[#f5f5f0]/50">{c.title}{c.deadline_day && <> · {c.deadline_label}: {c.deadline_day}</>}</span></span>
        <span className="flex flex-wrap gap-2">
          <button data-testid={`campaign-toggle-${c.id}`} onClick={toggle} className={live ? "rounded-sm border border-white/20 px-3 py-1.5 text-xs hover:border-[#c5a059]" : "rounded-sm bg-[#c5a059] px-3 py-1.5 text-xs font-semibold text-black hover:bg-[#d4af37]"}>{live ? "Hide Banner" : "Show Banner"}</button>
          <button data-testid={`campaign-preview-${c.id}`} onClick={() => send(true)} className={btn}>Email me a preview</button>
          <button data-testid={`campaign-send-${c.id}`} disabled={sent} onClick={() => send(false)} className={btn}>{sent ? "Sent this year" : "Send to Royal List"}</button>
        </span>
      </div>
      {c.id === "custom" && <CustomCampaignForm c={c} h={h} onDone={onDone} />}
    </div>
  );
};

const OrderDaysControl = ({ days, h, onDone }) => {
  const [v, setV] = useState(days ?? 4);
  useEffect(() => { if (days !== undefined) setV(days); }, [days]);
  const save = async () => { try { await axios.put(`${API}/admin/campaign/order-days`, { days: Number(v) }, h); toast.success("Countdown updated"); onDone(); } catch (e) { campErr(e); } };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-[#f5f5f0]/75">
      Last day to order is
      <input data-testid="order-days-input" type="number" min={0} max={21} value={v} onChange={(e) => setV(e.target.value)} className="w-16 rounded-sm border border-white/15 bg-black px-2 py-1 text-center text-[#f5f5f0] focus:border-[#c5a059] outline-none" />
      days before the holiday
      <button data-testid="order-days-save" onClick={save} className="rounded-sm border border-[#c5a059]/50 px-3 py-1 text-xs text-[#c5a059] hover:bg-[#c5a059]/10">Save</button>
    </div>
  );
};

export const CampaignCard = ({ token }) => {
  const [d, setD] = useState({ active: null, campaigns: [] });
  const h = { headers: { "X-Admin-Token": token } };
  const load = () => axios.get(`${API}/admin/campaigns`, h).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="admin-campaign-card" className="mt-4 rounded-sm border border-[#c5a059]/30 bg-[#121212] p-5">
      <div className="text-xs uppercase tracking-widest text-[#c5a059]">Holiday Campaigns</div>
      <p className="mt-1 text-sm text-[#f5f5f0]/70">Pick a holiday to show its section on your homepage (one at a time), and send a one-time email to your Royal List. Each email can be sent once per year.</p>
      <OrderDaysControl days={d.order_days} h={h} onDone={load} />
      <div className="mt-3">{d.campaigns.map((c) => <CampaignRow key={c.id} c={c} active={d.active} year={d.year} h={h} onDone={load} />)}</div>
    </div>
  );
};

export const ManualVipCard = ({ token, list, onDone }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const h = { headers: { "X-Admin-Token": token } };
  const err = (e) => toast.error(e.response?.data?.detail?.toString() || "Something went wrong.");
  const add = async () => {
    try {
      const r = await axios.post(`${API}/admin/vips`, { email, name }, h);
      navigator.clipboard?.writeText(r.data.link).catch(() => {});
      toast.success("VIP added — their private link is copied to your clipboard");
      setEmail(""); setName(""); onDone();
    } catch (e) { err(e); }
  };
  const remove = async (e) => { try { await axios.delete(`${API}/admin/vips/${encodeURIComponent(e)}`, h); onDone(); } catch (x) { err(x); } };
  const preview = async () => {
    if (!window.confirm("Email every VIP King their private early-access link now?")) return;
    try { const r = await axios.post(`${API}/admin/vip-preview`, {}, h); toast.success(`Sending private links to ${r.data.count} VIP King${r.data.count > 1 ? "s" : ""}`); } catch (e) { err(e); }
  };
  return (
    <div data-testid="admin-manual-vips" className="mt-4 rounded-sm border border-[#c5a059]/30 bg-[#121212] p-5">
      <div className="text-xs uppercase tracking-widest text-[#c5a059]">Hand-Picked VIP Kings</div>
      <p className="mt-1 text-sm text-[#f5f5f0]/70">Add family, early supporters or retail buyers as VIPs. Every VIP automatically gets their private link 7 days before launch, or send it now.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <input data-testid="vip-add-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="w-40 rounded-sm bg-black border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#c5a059]" />
        <input data-testid="vip-add-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="flex-1 min-w-[180px] rounded-sm bg-black border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#c5a059]" />
        <button data-testid="vip-add-btn" disabled={!email.includes("@")} onClick={add} className="rounded-sm bg-[#c5a059] px-4 py-2 text-sm font-semibold text-black hover:bg-[#d4af37] disabled:opacity-50">Add VIP</button>
      </div>
      <div className="mt-3 divide-y divide-white/5">
        {list.map((v) => (
          <div key={v.email} data-testid={`manual-vip-${v.email}`} className="flex items-center justify-between py-2 text-sm">
            <span>{v.name || "—"} <span className="ml-2 text-xs text-[#f5f5f0]/50">{v.email}</span></span>
            <button onClick={() => remove(v.email)} className="text-xs text-[#f5f5f0]/50 hover:text-red-300">Remove</button>
          </div>
        ))}
      </div>
      <button data-testid="vip-preview-send" onClick={preview} className="mt-3 rounded-sm border border-[#c5a059]/50 px-4 py-2 text-sm text-[#c5a059] hover:bg-[#c5a059]/10">Send VIP Preview Links Now</button>
    </div>
  );
};

const EMAIL_DOMAINS = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "icloud.com", "aol.com", "live.com", "msn.com", "me.com", "comcast.net", "att.net", "verizon.net", "protonmail.com", "ymail.com"];

const editDistance = (a, b) => {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++)
    d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return d[a.length][b.length];
};

export const emailSuggestion = (v) => {
  const m = /^([^@\s]+)@([^@\s]+)$/.exec(v.trim().toLowerCase());
  if (!m || EMAIL_DOMAINS.includes(m[2])) return null;
  const best = EMAIL_DOMAINS.map((d) => [d, editDistance(m[2], d)]).sort((x, y) => x[1] - y[1])[0];
  return best[1] <= 2 ? `${m[1]}@${best[0]}` : null;
};

export const EmailHint = ({ value, onPick, testid, className = "" }) => {
  const [touched, setTouched] = useState(false);
  useEffect(() => { if (!value) setTouched(false); else { const t = setTimeout(() => setTouched(true), 900); return () => clearTimeout(t); } }, [value]);
  if (!value || !touched) return null;
  const fix = emailSuggestion(value);
  if (fix) return (
    <p data-testid={`${testid}-suggestion`} className={`ritual-cap text-xs text-[#f5f5f0]/70 ${className}`}>
      Did you mean{" "}
      <button type="button" data-testid={`${testid}-suggestion-apply`} onClick={() => onPick(fix)} className="text-[#c5a059] underline underline-offset-4 hover:text-[#d4af37]">{fix}</button>?
    </p>
  );
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(value.trim())) return (
    <p data-testid={`${testid}-invalid`} className={`ritual-cap text-xs text-[#e8a87c] ${className}`}>That email looks incomplete, e.g. name@gmail.com</p>
  );
  return null;
};

const GIFT_PICKS = [
  ["For the man who keeps it sharp", "The Polished Prince", "$199", "products"],
  ["For the bearded man", "The Majestic Mane", "$275 · ships free", "products"],
  ["For the man who shaves", "The Regal Glow", "$188", "products"],
  ["Not sure what he needs?", "A Royal Gift Card", "$50 – $250", "gift-cards"],
];

export const GiftGuide = () => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { axios.get(`${API}/campaign`).then((r) => setShow(["christmas", "black_friday", "fathers_day", "valentines"].includes(r.data.active))).catch(() => {}); }, []);
  if (!show) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await axios.post(`${API}/subscribe`, { email: email.trim(), source: "gift-guide" }); setDone(true); }
    catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === "string" ? d : "Please enter a valid email."); }
    finally { setBusy(false); }
  };
  return (
    <section id="gift-guide" data-testid="gift-guide" className="relative bg-[#0a0a0a] py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid gap-14 lg:grid-cols-[1fr_1.1fr] items-center">
        <div>
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#c5a059]">The Royal Gift Guide</p>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-[#f5f5f0]">Gifts for Him</h2>
          <p className="mt-5 max-w-md text-[#f5f5f0]/70 font-light leading-relaxed">Not sure what to get him? Join the Royal List and we'll send our gift guide straight to your inbox: the right routine for every kind of man.</p>
          {done ? (
            <p data-testid="gift-guide-success" className="ritual-cap mt-8 font-serif italic text-xl text-[#d4af37]">Your gift guide is on its way. Check your inbox.</p>
          ) : (
            <form onSubmit={submit} data-testid="gift-guide-form" className="mt-8 max-w-md">
              <div className="flex flex-col sm:flex-row gap-3">
                <input data-testid="gift-guide-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
                  className="flex-1 rounded-sm bg-black/60 border border-white/10 px-4 py-3.5 text-sm text-[#f5f5f0] placeholder:text-[#f5f5f0]/30 focus:border-[#c5a059] focus:outline-none focus:ring-1 focus:ring-[#c5a059] transition-colors" />
                <button data-testid="gift-guide-submit" disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] px-6 py-3.5 text-sm font-semibold text-black hover:bg-[#d4af37] transition-colors disabled:opacity-60">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Gift size={15} />} Send Me the Guide
                </button>
              </div>
              <EmailHint value={email} onPick={setEmail} testid="gift-guide-email" className="mt-2" />
              <p className="mt-3 text-xs text-[#f5f5f0]/40">One email with the guide. No spam, unsubscribe any time.</p>
            </form>
          )}
        </div>
        <ol className="grid gap-px rounded-sm overflow-hidden border border-[#c5a059]/25 bg-[#c5a059]/15">
          {GIFT_PICKS.map(([who, what, price, target], i) => (
            <li key={what}>
              <button data-testid={`gift-pick-${i}`} onClick={() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth" })}
                className="group flex w-full items-baseline gap-6 bg-[#0d0d0d] px-7 py-6 text-left transition-colors duration-300 hover:bg-[#141210]">
              <span className="w-10 shrink-0 font-serif text-3xl tabular-nums text-[#c5a059]/70">0{i + 1}</span>
              <span className="flex-1">
                <span className="block text-[11px] uppercase tracking-[0.25em] text-[#f5f5f0]/50">{who}</span>
                <span className="mt-1 block font-serif text-2xl text-[#f5f5f0]">{what}</span>
                <span className="mt-1 block text-sm text-[#c5a059]">{price}</span>
              </span>
              <span className="self-center text-[#c5a059] opacity-0 -translate-x-2 transition-[opacity,transform] duration-300 group-hover:opacity-100 group-hover:translate-x-0">→</span>
              </button>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};
